import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {API_URL} from '../constants';
import {map} from 'rxjs/operators';

export interface ChapterDirectoryEntry {
    id: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    memberType?: string;
    photouri?: string;
}

@Injectable({
    providedIn: 'root',
})
export class UserApiService {
    private apiUrl = `${API_URL}` + 'users'; // Replace with your API URL

    constructor(private http: HttpClient) {
    }

    // Call the backend API to validate login credentials
    getData(id: string): Observable<any> {
        return this.http.get<any>(this.apiUrl + '/' + id);
    }

    getShortData(id: string): Observable<any> {
        return this.http.get<any>(this.apiUrl + '/short/' + id);
    }

    getUsersBatch(userIds: string[]): Observable<any[]> {
        return this.http.post<any[]>(this.apiUrl + '/batch', userIds);
    }


    getUserByEmail(profileData: { email: string }): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/email', profileData);
    }

    verifyUserEmail(profileData: { email: string }): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/verify-email', profileData);
    }

    registerUser(userData: any): Observable<any> {
        return this.http.post<any>(this.apiUrl, userData);
    }

    // Call the backend API to validate login credentials
    updateProfile(id: string, profileData: any): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/' + id, profileData);
    }

    updatePassword(id: string, passwordData: any): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/' + id, passwordData);
    }

    uploadImage(id: string, imageData: FormData): Observable<any> {
        return this.http.put<any>(`${API_URL}` + 'profile/upload/' + id, imageData);
    }

    uploadBase64Image(id: string, base64Image: string, filename: string): Observable<any> {
        const payload = {
            base64Image: base64Image,
            filename: filename
        };
        return this.http.post<any>(`${API_URL}profile/upload-base64/${id}`, payload);
    }

    // Call the backend API to validate login credentials
    login(loginData: { email: string; password: string }): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/login', loginData);
    }

    getRole(role: string): Observable<any> {
        return this.http.get<any>(`${API_URL}` + 'role-permission/permissions/' + role);
    }

    // Call the backend API to validate login credentials
    forgotPassword(email: { email: string }): Observable<any> {
        return this.http.put(this.apiUrl + '/forgot-password', email, {responseType: 'text'});
    }

    // NEC executive committee editing another member's full profile (API: USER:UPDATE only).
    updateProfileAsAdmin(id: string, profileData: any): Observable<any> {
        return this.http.put<any>(this.apiUrl + '/' + id + '/admin-profile', profileData);
    }

    // After Stripe membership checkout: the API verifies the session was paid by this user
    // and marks the registration ACTIVE / PAYMENT_COMPLETE.
    confirmMembershipPayment(id: string, sessionId: string): Observable<any> {
        return this.http.post<any>(this.apiUrl + '/' + id + '/confirm-membership-payment', {sessionId});
    }

    getUsersByChapterId(id: string): Observable<any> {
        return this.http.get<any>(this.apiUrl + '/get-chapter-users/' + id);
    }

    // Contact details + full address; the API only serves this to the chapter's executive committee (and NEC).
    getChapterDirectory(chapterId: string): Observable<ChapterDirectoryEntry[]> {
        return this.http.get<ChapterDirectoryEntry[]>(this.apiUrl + '/chapter-directory/' + chapterId);
    }

//get all users
    getUsers(): Observable<any> {
        return this.http.get<any>(this.apiUrl);
    }

    getProfileImageAsBlob(imageUrl: string): Observable<Blob> {
        const headers = new HttpHeaders({
            'Accept': 'image/jpeg'
        });

        return this.http.get(imageUrl, {
            headers,
            responseType: 'blob'
        });
    }

    deleteUserNotification(userId: string, notificationId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${userId}/delete-notification/${notificationId}`);
    }

    getProfileImageAsBase64(url: string): Observable<string> {
        return this.http.get<any>(url).pipe(
            map(response => {
                if (response && response.isBase64Encoded && response.body) {
                    return `data:image/jpeg;base64,${response.body}`;
                }
                return '';
            })
        );
    }

}
 